import { IPoint2d } from '@/editor/math/IPoint2d';
import { CommonController2d } from '../CommonController';
import VirtualWallModel, { EVirtualWallStatus } from '@/editor/entities/2d/virtualWallModel';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { EEntityType, EOperateMode, EToolType } from '@/editor/types';
import { WallPointAbsorbManager } from '@/editor/manager/absorb/wall/wallPointAbsorbManager';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { ESepcialKey } from '@/editor/stores/keyboardStore';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { MAX_CHORD_LENGTH, MAX_SAGITTA, MIN_CHORD_LENGTH, MIN_SAGITTA } from '@/editor/constant/wall';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { clientPosToWorldPos } from '@/editor/utils';
import { getRoomId } from '@/editor/data/room/utils';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { convertSepWallTypeToVirtualWallStyle } from '@/editor/utils/wall';
import { getPathFromEdges } from '@/editor/utils/edge';
import { getPointAndClosedRelation } from '@/editor/math/shape/path';
import { EPointAndClosedRelation } from '@/editor/types/shape';
import { FloatingPoint } from '@/editor/math/number';
import { showToast } from '@/editor/utils/feedback';
import i18n from '@/i18n';

enum EDrawArcSeparateWallStage {
  Start = 1, // 绘制起始点
  End = 2, // 绘制结束点
  Sagitta = 3 // 绘制弦高
}

interface IAddSeparateWallPoint {
  stage: EDrawArcSeparateWallStage.End | EDrawArcSeparateWallStage.Start;
  point: IPoint2d;
}

interface IAddSeparateWallSagitta {
  stage: EDrawArcSeparateWallStage.Sagitta;
  sagitta: number;
}

type TAddSeparateWallPoint = IAddSeparateWallPoint | IAddSeparateWallSagitta;

class DrawArcSeparateWall extends CommonController2d {
  protected _movePoint?: IPoint2d | null;

  protected _startPoint?: IPoint2d | null;
  protected _endPoint?: IPoint2d | null;
  protected _sagitta?: number | null;
  private _relateRoomId?: string | null;
  private stage: EDrawArcSeparateWallStage = EDrawArcSeparateWallStage.Start; // 记录当前在哪个绘制阶段

  private _virtualWallModel = new VirtualWallModel();
  private _virtualRulerModel: RulerModel;

  private _adsorbManager = new WallPointAbsorbManager(globalStores.separateWallStore, EEntityType.SeparateWall);
  private _eventDispose: () => void;

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this._virtualRulerModel = new RulerModel({
      onCommit: this.handleRulerCommit.bind(this)
    });

    this._adsorbManager.updateWallData();

    this._eventDispose = editorEvent.on(EditorEventName.KEYBOARD_KEY_PRESS, (key: string) => {
      if (key === ESepcialKey.Escape) {
        if (this.stage === EDrawArcSeparateWallStage.Start) {
          this._gs.operateInfo = { mode: EOperateMode.EMPTY, toolType: EToolType.empty };
        } else {
          this.clear();
          this.stage = EDrawArcSeparateWallStage.Start;
        }
      }
    });
  }

  private getSeparateWallType() {
    return globalStores.operateInfo.toolType === EToolType.virtualSeparateWall
      ? ESeparateWallType.VirtualWall
      : globalStores.paramStore.separateWallType;
  }

  private getSeparateWallThickness() {
    return this.getSeparateWallType() === ESeparateWallType.VirtualWall
      ? 0
      : globalStores.paramStore.separateWallThickness;
  }

  private isPointInRelateRoom(point: IPoint2d) {
    if (!this._relateRoomId) return false;

    const roomData = globalStores.roomStore.getRoomData(this._relateRoomId);
    if (!roomData) return false;

    const roomPath = getPathFromEdges(roomData.pathInfos);
    const holePaths = roomData.holeInfos.map((hole) => getPathFromEdges(hole));
    const inRoom = getPointAndClosedRelation(point, roomPath) !== EPointAndClosedRelation.Outside;
    const inHole = holePaths.some((path) => getPointAndClosedRelation(point, path) === EPointAndClosedRelation.Inside);

    return inRoom && !inHole;
  }

  private setStartPoint(point: IPoint2d, roomId: string) {
    this._relateRoomId = roomId;
    this._startPoint = point;
    this.stage = EDrawArcSeparateWallStage.End;
    this._adsorbManager.setOrthogonalData(point);
    this._adsorbManager.lockSeparateWallRoom(roomId);
  }

  private handleRulerCommit(id: string, value: number) {
    switch (this.stage) {
      case EDrawArcSeparateWallStage.Start:
        break;
      case EDrawArcSeparateWallStage.End: {
        if (!this._movePoint || !this._startPoint) break;
        const direction = Point2dUtils.subtracted(this._movePoint, this._startPoint);
        const newEndPoint = Point2dUtils.addDistance(this._startPoint, value, direction);
        void this.addSeparateWallPoint({ point: newEndPoint, stage: this.stage });
        break;
      }
      case EDrawArcSeparateWallStage.Sagitta: {
        const sagitta = Math.sign(this._sagitta || 1) * value;
        void this.addSeparateWallPoint({ sagitta, stage: this.stage });
        break;
      }
    }
  }

  protected async addSeparateWallPoint(params: TAddSeparateWallPoint) {
    switch (params.stage) {
      case EDrawArcSeparateWallStage.Start:
        {
          const roomId =
            this._relateRoomId && this.isPointInRelateRoom(params.point)
              ? this._relateRoomId
              : getRoomId(globalStores.roomStore.getRoomDatas(), params.point);
          if (!roomId) return;

          this.setStartPoint(params.point, roomId);
        }
        break;
      case EDrawArcSeparateWallStage.End: {
        if (!this._startPoint) return;

        const chordLength = Point2dUtils.distanceTo(this._startPoint, params.point);

        if (FloatingPoint.lessThan(chordLength, MIN_CHORD_LENGTH)) return;

        const endPoint = FloatingPoint.greaterThanOrEqual(chordLength, MAX_CHORD_LENGTH)
          ? Point2dUtils.addDistance(
              this._startPoint,
              MAX_CHORD_LENGTH,
              Point2dUtils.getDirection(this._startPoint, params.point)
            )
          : params.point;

        if (!this.isPointInRelateRoom(endPoint)) {
          showToast(i18n.t('draw.warning.sep_wall_intersecting'));
          return;
        }

        this._endPoint = endPoint;
        this._sagitta = Point2dUtils.distanceTo(this._startPoint, endPoint) / 2;
        this._movePoint = null;

        this.stage = EDrawArcSeparateWallStage.Sagitta;
        this._adsorbManager.clearModel();
        break;
      }

      case EDrawArcSeparateWallStage.Sagitta: {
        if (!this._startPoint || !this._endPoint || !this._relateRoomId) return;

        const sagitta = Math.abs(params.sagitta);
        if (FloatingPoint.lessThan(sagitta, MIN_SAGITTA)) return;

        this._sagitta = FloatingPoint.greaterThanOrEqual(sagitta, MAX_SAGITTA)
          ? MAX_SAGITTA * Math.sign(params.sagitta || 1)
          : params.sagitta;
        const arc = new ArcSeg2d({ start: this._startPoint, end: this._endPoint, sagitta: this._sagitta });
        const nextStartPoint = this._endPoint;
        const nextRoomId = this._relateRoomId;
        const isAddSuccess = this._gs.separateWallOperation.addSeparateWall(
          this._startPoint,
          this._endPoint,
          this._relateRoomId,
          {
            center: arc.center,
            clockwise: arc.clockwise
          }
        );

        if (!isAddSuccess) {
          showToast(i18n.t('draw.warning.sep_wall_intersecting'));
          return;
        }

        this.clear();

        this._adsorbManager.updateWallData();
        this.stage = EDrawArcSeparateWallStage.Start;
        this._relateRoomId = nextRoomId;
        void this.addSeparateWallPoint({ point: nextStartPoint, stage: EDrawArcSeparateWallStage.Start }); // 连续绘制
        break;
      }
    }

    this.updateVirtualModel();
  }

  protected override mouseUp(e: MouseEvent, isDragged: boolean) {
    if (isDragged) return;

    if (e.button === 2) {
      this.backward();
      return;
    }

    if (e.button !== 0) return;

    switch (this.stage) {
      case EDrawArcSeparateWallStage.Start:
      case EDrawArcSeparateWallStage.End: {
        if (!this._movePoint) break;
        void this.addSeparateWallPoint({ point: this._movePoint, stage: this.stage });
        break;
      }
      case EDrawArcSeparateWallStage.Sagitta: {
        if (!this._sagitta) break;
        void this.addSeparateWallPoint({ sagitta: this._sagitta, stage: this.stage });
        break;
      }
    }
  }

  private backward() {
    switch (this.stage) {
      case EDrawArcSeparateWallStage.Start:
        this.dispose();
        this.exit();
        break;
      case EDrawArcSeparateWallStage.End:
      case EDrawArcSeparateWallStage.Sagitta:
        this.clear();
        this.stage = EDrawArcSeparateWallStage.Start;
        break;
    }
  }

  protected override mouseMove(e: MouseEvent) {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    let point = clientPosToWorldPos(e, camera, renderer);

    if (this.stage === EDrawArcSeparateWallStage.Sagitta) {
      if (!this._startPoint || !this._endPoint) return;

      const absorbPoint = this._adsorbManager.calculateArcAbsorbPointThenRender({
        arcStart: this._startPoint,
        arcEnd: this._endPoint,
        rawPoint: point
      });

      if (absorbPoint) point = absorbPoint;

      let sagitta = ArcSeg2d.calculateSagitta(this._startPoint, this._endPoint, point);
      if (Math.abs(sagitta) < MIN_SAGITTA) {
        sagitta = MIN_SAGITTA * Math.sign(sagitta);
        this._adsorbManager.clearModel();
      } else if (Math.abs(sagitta) > MAX_SAGITTA) {
        sagitta = MAX_SAGITTA * Math.sign(sagitta);
        this._adsorbManager.clearModel();
      }

      this._sagitta = sagitta;
      this._movePoint = null;
    } else {
      const absorbPoint = this._adsorbManager.calculateAbsorbPointThenRender(point);
      if (absorbPoint) {
        point = absorbPoint;
      }

      if (this.stage === EDrawArcSeparateWallStage.End && this._startPoint) {
        const chordLength = Point2dUtils.distanceTo(this._startPoint, point);
        if (FloatingPoint.greaterThan(chordLength, MAX_CHORD_LENGTH)) {
          point = Point2dUtils.addDistance(
            this._startPoint,
            MAX_CHORD_LENGTH,
            Point2dUtils.getDirection(this._startPoint, point)
          );
          this._adsorbManager.clearModel();
        }
      }

      this._movePoint = point;
      this._sagitta = null;
    }

    this.updateVirtualModel();
  }

  private updateVirtualModel() {
    const separateWallType = this.getSeparateWallType();
    const thickness = this.getSeparateWallThickness();
    const style = convertSepWallTypeToVirtualWallStyle(separateWallType);

    switch (this.stage) {
      case EDrawArcSeparateWallStage.Start:
        break;
      case EDrawArcSeparateWallStage.End:
        if (this._startPoint && this._movePoint) {
          const chordLength = Point2dUtils.distanceTo(this._startPoint, this._movePoint);
          const isLegal =
            this.isPointInRelateRoom(this._movePoint) &&
            chordLength >= MIN_CHORD_LENGTH &&
            chordLength <= MAX_CHORD_LENGTH;

          this._virtualWallModel.update([
            {
              start: this._startPoint,
              end: this._movePoint,
              thickness,
              style,
              status: isLegal ? EVirtualWallStatus.Normal : EVirtualWallStatus.Unnormal
            }
          ]);

          this._virtualRulerModel.resetFocusId();
          this._virtualRulerModel.updateModel(
            [
              {
                id: '1',
                start: this._startPoint,
                end: this._movePoint,
                editable: true,
                focusTrap: true,
                max: MAX_CHORD_LENGTH
              }
            ],
            '1'
          );
        }
        break;
      case EDrawArcSeparateWallStage.Sagitta:
        if (this._startPoint && this._endPoint && this._sagitta) {
          // 知道起点、终点、线上点，计算弦高
          const arc = new ArcSeg2d({
            start: this._startPoint,
            end: this._endPoint,
            sagitta: this._sagitta
          });
          const isLegal = this._relateRoomId
            ? this._gs.separateWallOperation.isLegal(this._startPoint, this._endPoint, this._relateRoomId, {
                center: arc.center,
                clockwise: arc.clockwise
              }).isLegal
            : false;

          this._virtualWallModel.update([
            {
              start: this._startPoint,
              end: this._endPoint,
              thickness,
              center: arc.center,
              clockwise: arc.clockwise,
              style,
              status: isLegal ? EVirtualWallStatus.Normal : EVirtualWallStatus.Unnormal
            }
          ]);

          this._virtualRulerModel.resetFocusId();
          this._virtualRulerModel.updateModel(
            [
              { id: '1', start: this._startPoint, end: this._endPoint, offset: 0 },
              {
                id: '2',
                start: this._startPoint,
                end: this._endPoint,
                center: arc.center,
                clockwise: arc.clockwise,
                editable: true,
                focusTrap: true,
                offset: 0,
                max: MAX_SAGITTA
              }
            ],
            '2'
          );
        }
        break;
    }
  }

  clear() {
    this._virtualWallModel.clear();
    this._virtualRulerModel.clear();
    this._adsorbManager.clearOrthogonalData();
    this._adsorbManager.clearModel();
    this._adsorbManager.clearSeparateWallRoom();

    this._movePoint = null;
    this._startPoint = null;
    this._endPoint = null;
    this._sagitta = null;
    this._relateRoomId = undefined;
  }

  override dispose() {
    this.clear();
    this._eventDispose();
    this._adsorbManager.dispose();
    this._virtualWallModel.dispose();
    this._virtualRulerModel.dispose();
    super.dispose();
  }
}

export default DrawArcSeparateWall;
